<template>
  <section id="demand-v2-options-section" class="grid-block">
    <header>
      <span>Варианты</span>
      <VButton
        v-if="!hideCreateOption"
        theme="link"
        :disabled="demandV2.isOptionsExists"
        @click="addOption"
      >
        Добавить вариант +
      </VButton>
    </header>

    <div class="content">
      <VDataGridV2
        :data="demandV2.options"
        :context-menu="demandOptionContextMenu"
        layout="fill"
        @row:dblclick="(row) => DemandsV2Service.editOption(
          row, // demandV2Option
          () => $emit(DemandOption.Events.Updated),
          () => $emit(DemandOption.Events.Deleted),
        )"
      >
        <VDataGridColumnV2
          id="is-chosen-option"
          content-align="right"
          width="10px"
        >
          <template #default="{ row }">
            <VLink
              :disabled="demandV2.isApproved"
              @click.stop="toggleOption(row)"
            >
              <template #icon>
              <span class="material-icons">
                {{ row.isChosen ? 'check_circle' : 'radio_button_unchecked' }}
              </span>
              </template>
            </VLink>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="name"
          width="80px"
          content-class="ellipsis"
        >
          <template #label>
            Наименование
            <div class="description">
              Производитель
            </div>
          </template>
          <template #default="{ row }">
            <div
              class="ellipsis"
              :title="row.name"
            >
              {{ row.name }}
            </div>
            <div
              class="ellipsis description"
              :title="row.manufacturer?.name"
            >
              {{ row.manufacturer?.name || '--' }}
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="price"
          width="100px"
          content-align="right"
          content-class="ellipsis"
        >
          <template #label>
            Цена с НДС
            <div class="ellipsis description">
              В валюте предл.
            </div>
          </template>
          <template #default="{ row }">
            <div
              class="ellipsis"
              :title="row.priceFullSeller?.formatInDisplayCurrency(row.offerCurrencyCode, row.getDateForCurrencyRate())"
            >
              {{ row.priceFullSeller?.formatInDisplayCurrency(row.offerCurrencyCode, row.getDateForCurrencyRate()) }}
            </div>
            <div
              class="description"
              :title="row.priceFullSeller?.format(row.offerCurrencyCode)"
            >
              {{ row.priceFullSeller?.format(row.offerCurrencyCode) }}
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="qtyAvailable"
          content-align="right"
          content-class="ellipsis"
          width="100px"
        >
          <template #label>
            Доступное <br> количество
          </template>
          <template #default="{ row }">
            <div
              class="ellipsis"
              :title="row.qtyAvailable.format(row.unit) || '--'"
            >
              {{ row.qtyAvailable.format(row.unit) || '--' }}
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          v-if="demandV2.isChosenMoreThen1Option"
          id="qtySelected"
          content-align="right"
          width="100px"

          @edit="(row, anchor) => onChangeQtySelected(row, anchor)"
        >
          <template #label>
            Выбранное <br> количество
          </template>
          <template #default="{ row }">
              <div class="qty-selected">
                <VIcon
                  v-if="row.qtySelected.greaterThan(row.qtyAvailable)"
                  material="warning"
                  class="qty-exceeded-icon"
                  theme="outlined"
                  :title="getQtySelectedTitle(row)"
                />

                <div
                  class="ellipsis qty-selected-value"
                  :title="row.qtySelected?.format(row.unit)"
                >
                  {{ row.qtySelected?.format(row.unit) }}
                </div>
              </div>
              <div
                v-if="row.qtySelected.greaterThan(row.qtyAvailable)"
                class="description"
              >
                Выбранное количество превышает доступное
              </div>
          </template>
        </VDataGridColumnV2>
      </VDataGridV2>
    </div>
  </section>
</template>

<style lang="scss">
#demand-v2-options-section {
  td[data-column-id="qtySelected"] {

    > .edit-pencil {
      left: 8px;
      right: unset;
    }
  }

  .qty-selected {
    display:         flex;
    justify-content: flex-end;
  }

  .qty-selected-value {
    width: fit-content;
  }

  .qty-exceeded-icon {
    color: var(--warning-strong);
  }

  .qty-edit-icon {
    color: var(--accent-strong);
  }
}
</style>

<script lang="ts">

import DemandOption                 from '@/domains/DemandsV2/DemandOption';
import DemandV2                     from '@/domains/DemandsV2/DemandV2';
import DemandsV2Service             from '@/domains/DemandsV2/DemandsV2Service';

import type ContextMenuItem         from '@/lib/components/VContextMenu/ContextMenuItem';

import ChangeDemandV2OptionQtyPopup from '@/domains/DemandsV2/components/DemandV2Card/ChangeDemandV2OptionQtyPopup.vue';

export default defineComponent({
  name: 'DemandV2OptionsSection',

  components: {
    ChangeDemandV2OptionQtyPopup,
  },

  props: {
    demandV2: {
      type:     DemandV2,
      required: true,
    },
    hideCreateOption: {
      type:    Boolean,
      default: false,
    },
  },

  emits: [
    DemandOption.Events.Created,
    DemandOption.Events.Updated,
    DemandOption.Events.Deleted,
  ],

  data() {
    return {
      DemandOption,
      DemandsV2Service,

      // optionQtyPopup: {
      //   target: null as HTMLElement | null,
      //   row:    null as DemandOption | null,
      // },
    };
  },

  computed: {
    /** Меню варианта. Логика пунктов — в сервисе; колбэки-эмиты прокидывает компонент. */
    demandOptionContextMenu(): ContextMenuItem<DemandOption>[] {
      return DemandsV2Service.getDemandOptionContextMenu(
        () => this.$emit(DemandOption.Events.Updated),
        () => this.$emit(DemandOption.Events.Deleted),
      );
    },
  },

  methods: {
    /** Подсказка на ячейке выбранного количества. Сравнение через greaterThan:
     *  оператор > на Numeric не работает, valueOf у него кидает. */
    getQtySelectedTitle(row: DemandOption): string {
      if (row.qtySelected.greaterThan(row.qtyAvailable)) {
        return 'Выбранное количество превышает доступное';
      }
      else {
        return '';
      }
    },

    addOption(): void {
      DemandsV2Service.createOption(
        this.demandV2,
        () => this.$emit(DemandOption.Events.Created),
      );
    },

    async toggleOption(option: DemandOption): Promise<void> {
      option.choose();
      await DemandsV2Service.updateOption(option);

      this.$emit(DemandOption.Events.Updated);
    },

    onChangeQtySelected(row: DemandOption, anchor): void {
      this.$core.dialogs.ChangeDemandV2OptionQtyPopup.hideIfOpen()
      this.$core.dialogs.ChangeDemandV2OptionQtyPopup.show({
        target: anchor,
        row: row,
      })
    },
  },
});
</script>
